/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, Redirect, withRouter } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import injectSaga from 'utils/injectSaga';
import { createStructuredSelector } from 'reselect';
import { AuthenticationFailedError, ConnectionFailedError } from '@pearsontechnology/aero-rtm';
import { TRACKER_NAMES, startRecordSession } from '@pearsontechnology/bronte-lib-fullstory';
import orderBy from 'lodash/orderBy';
import isEqual from 'lodash/isEqual';
import { ThemeProvider } from 'styled-components';
import { Helmet } from 'react-helmet';

import ConnectionManager from 'ConnectionManager';
import accountService from 'services/AccountService';
import activityService from 'services/ActivityService';
import annotationService from 'services/AnnotationService';
import scenarioService from 'services/ScenarioService';
import pluginService from 'services/PluginService';
import templateService from 'services/TemplateService';
import pathwayService from 'services/PathwayService';
import interactiveService from 'services/InteractiveService';
import componentService from 'services/ComponentService';
import workspaceService from 'services/WorkspaceService';
import knowledgeService from 'services/KnowledgeService';
import projectService from 'services/ProjectService';
import scopeService from 'services/ScopeService';
import favoritesService from 'services/FavoritesService';
import recentService from 'services/RecentService';
import themeService from 'services/ThemeService';
import publicationService from 'services/PublicationService';
import exportService from 'services/ExportService';
import previewService from 'services/PreviewService';
import undoRedoService from 'services/UndoRedoService';
import aiSummaryService from 'services/AiSummaryService';
import { getBannerNotificationFeatureKeyDetails } from 'services/BannerNotificationService';
import assetManager from 'AssetManager';
import 'ScopeBindingManager';
import appConfig from 'appConfig';

import CornucopiaScreenEditor from 'builder/Pathway/components/ScreenEditor/Cornucopia/Loadable';

import ToastManager from 'containers/ToastManager';
import { pushToast } from 'containers/ToastManager/actions';
import makeSelectConnectionManager from 'containers/ConnectionManager/selectors';
import makeSelectCurrentUser from 'data/CurrentUser/selectors';
import { Provider as FileUploadProvider } from 'containers/FileUploadProvider';
import { ServicesProvider } from 'containers/ServicesProvider';
import messages from 'containers/ConnectionManager/messages';
import connectionSaga from 'containers/ConnectionManager/saga';
import {
  serverConnect,
  serverConnectError,
} from 'containers/ConnectionManager/actions';

import featureFlagService, { featureKeys } from 'services/FeatureFlagService';
import gtmService from 'utils/gtm/gtmService';
import { EDITOR } from 'pluginServices/constants/context';
import LoginPage from 'components/LoginPage';

import currentUserSaga from 'data/CurrentUser/saga';
import pluginLoader from 'data/Plugin/pluginLoader';
import { load, save, remove } from 'data/browserStorage';
import { LOCAL_STORAGE, SESSION_STORAGE } from 'data/browserStorage/constants';
import RecentPage from 'Workspace/components/RecentPage';
import FavoritesPage from 'Workspace/components/FavoritesPage';
import HomePage from 'Workspace/components/HomePage';
import UnAuthorizedPage from 'components/UnAuthorizedPage';
import UnAvailableContent from 'components/UnAvailableContent';
import BannerNotification from 'components/BannerNotification';
import Plugins from 'Workspace/Plugins';
import Publications from 'Workspace/Publications';
import Admin from 'Workspace/Admin';
import Owner from 'Workspace/Owner';
import Cohorts from 'Workspace/Cohorts';
import Knowledge from 'Workspace/Knowledge';
import PlaceholderPage from 'Workspace/components/PlaceholderPage';
import workspaceShape from 'Workspace/types/workspace';
import Projects from 'Workspace/Projects';
import faviconLight from 'images/favicon-light.png';
import faviconDark from 'images/favicon-dark.png';

import {
  selectWorkspacesList,
  selectWorkspacesLoadingState,
} from 'Workspace/Admin/store/selectors';

import CopyContainer from 'containers/Clipboard';
import { RTM_ME_GET } from 'containers/ConnectionManager/constants';
import { NOTIFICATION_POLLING_INTERVAL, NOTIFICATION_DISMISS_TIME, notificationTypes, IES_V2_AUTH, IES_V2_AUTH_VALUE } from 'constants/constants';
import CourseBuilderContainer from 'builder/Course/containers/CourseBuilderContainer/Loadable';
import LessonBuilderContainer from 'builder/Lesson/containers/LessonBuilderContainer/Loadable';
import DashboardBuilderContainer from 'builder/Dashboard/containers/DashboardBuilderContainer/Loadable';
import ActivityBuilderContainer from 'builder/Activity/containers/ActivityBuilderContainer/Loadable';
import { GlobalStyle } from '../../globalStyles';
import { UserRoles as roles } from '../../constants/enums';

import appMessages from './messages';
import { makeSelectWorkspaceTheme, getReviewBanner } from './selectors';
import { changeWorkspaceTheme, updateNotificationHeight, toggleReviewerBanner } from './actions';
import themes from './themes';
import AppWrapper, { Loader } from './AppWrapper';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';
import enhanceWithSessionExpiryCheck from 'hocs/enhanceWithSessionExpiryCheck';
import RouterMiddleware from './RouterMiddleware';

class App extends React.Component {
  constructor(props) {
    super(props);
    this.connectionManager = new ConnectionManager();
    this.notificationInterval = null;
    this.resolveIsUserInfoAvailablePromise = null;
    this.resolveAreFeatureFlagsAvailablePromise = null;
    this.state = {
      preferredTheme: 'light',
      showBannerNotification: false,
      notificationType:'',
      notificationMessage:'',
      bannerHeight:'',
      isIesV2Loading: false,
      connection : null,
    };
  }

  componentDidMount() {
    this.initializeFullStory();
    this.getInitialTheme();
    this.processIesV2Token();
  }

  /**
   * We need to initiate FullStory even in the absence of an active user to proactively monitor and identify any potential issues that may arise during the login process
   */
  initializeFullStory = () => {
    const isUserInfoAvailablePromise = new Promise((resolve) => {this.resolveIsUserInfoAvailablePromise = resolve;});
    const areFeatureFlagsAvailablePromise  = new Promise((resolve) => {this.resolveAreFeatureFlagsAvailablePromise = resolve;});
    const TrackerCDPMock = {
      register: () => Promise.resolve(),
      context: () => ({ get: async () => EDITOR }),
      features: () => ({ is:  (featureName) => areFeatureFlagsAvailablePromise.then((getFeatureFlagValue) => getFeatureFlagValue(featureName))}),
      user: () => ({ get:() => isUserInfoAvailablePromise.then((user)=> ({id: user.accountId,...user}))}),
      cohort: () => ({ get:() => isUserInfoAvailablePromise.then((user) => ({piUserId: user.accountId}))}),
    };
    startRecordSession(TrackerCDPMock, 'workspace', { trackers: [TRACKER_NAMES.USER_TRACKER] });
  }
  componentDidUpdate(prevProps) {
    if (!this.props.areFeatureFlagsAvailable) return;
    this.resolveAreFeatureFlagsAvailablePromise?.(this.props.getFeatureFlagValue);
    if (prevProps.workspaces) {
      if (!isEqual(prevProps.workspaces.sort(), this.props.workspaces.sort()) ||
         this.props.workspacesLoadingState !== prevProps.workspacesLoadingState) {
        this.selectWorkspace(this.props);
      }

      if(this.isNotificationBannerFeatureEnabled()) {
        const { session : isUserSession } = this.props.currentUser;
        if(!this.isPreviewPage() && isUserSession && !this.notificationInterval) {
          //getBannerNotificationDetails will display notification banner if feature flag is enabled.
          this.getBannerNotificationDetails();
          //We are creating interval to check for any changes to notification description in launch darkly.
          this.notificationInterval = setInterval(() => this.getBannerNotificationDetails(), NOTIFICATION_POLLING_INTERVAL);
        }

        if(!isUserSession && this.notificationInterval) {
          //reset notification banner if user logged of from system.
          this.resetNotificationBannerInfo();
        }
      }
    }
  }

  componentWillUnmount() {
    this.themeQuery.removeListener(this.handlePreferredThemeChange);
    clearInterval(this.notificationInterval);
  }

   isPreviewPage = () => {
     if (window.location.href.indexOf("/preview") > -1 ||
     window.location.href.indexOf("/epub-reader") > -1) {
       return true;
     }

     return false;
   }

   isNotificationBannerFeatureEnabled = () => {
     const { getFeatureFlagValue } = this.props;
     return (
       getFeatureFlagValue(featureKeys.FEATURE_FLAG_BRNT_19838_WORKSPACE_ALERT_BANNER_DO_NOT_DELETE) ||
      getFeatureFlagValue(featureKeys.BRNT_19837_WORKSPACE_NOTIFICATION_BANNER_DO_NOT_DELETE) ||
      getFeatureFlagValue(featureKeys.BRNT_19839_WORKSPACE_WARNING_BANNER_DO_NOT_DELETE)
     );
   }

   resetNotificationBannerInfo = () => {
     if(this.notificationInterval) {
       clearInterval(this.notificationInterval);
       this.notificationInterval = null;
     }

     Object.values(notificationTypes).forEach((notificationType) => {
       remove(`dismiss${notificationType}Time`, SESSION_STORAGE);
       remove(`${notificationType}Message`, SESSION_STORAGE);
     });
   }

   getInitialTheme = () => {
     const { setTheme } = this.props;
     const storedTheme = localStorage.getItem('workspaceTheme');

     // Get the OS preferred theme
     this.themeQuery = window.matchMedia('(prefers-color-scheme: dark)');
     this.themeQuery.addListener(this.handlePreferredThemeChange);
     const preferredTheme = this.themeQuery.matches ? 'dark' : 'light';

     // Use the OS preferred theme if there is no stored theme
     const currentTheme = storedTheme || preferredTheme;
     setTheme(currentTheme);
     this.setState({ preferredTheme });
   }

   UNSAFE_componentWillMount() {
     featureFlagService.initializeFeatureFlags();
     const isIesV2Auth = load(IES_V2_AUTH, LOCAL_STORAGE) === 'true';
     const iesV2AuthValue = load(IES_V2_AUTH_VALUE, LOCAL_STORAGE);
     
     if (this.props.currentUser.bearerToken && !isIesV2Auth) {
       this.isAutoConnecting = true;
       const credentials = { bearerToken: this.props.currentUser.bearerToken };
       this.connectRTM(credentials, false);
     }

     if (iesV2AuthValue) {
       this.isAutoConnecting = true;
       const parsedIesV2AuthValue = JSON.parse(iesV2AuthValue);
       this.handleLoginIesV2(parsedIesV2AuthValue);
     }
   }

   handlePreferredThemeChange = () => {
     if (this.themeQuery.matches) {
       this.setState({ preferredTheme: 'dark' });
     } else {
       this.setState({ preferredTheme: 'light' });
     }
   };

   selectWorkspace({ workspaces, workspacesLoadingState, dispatch }) {
     const selectedWorkspace = workspaceService.getSelectedWorkspaceId();
     if (!workspaces || workspacesLoadingState) {
       return;
     }

     if (!workspaces.length) {
       dispatch(pushToast({
         type: 'simple',
         message: <FormattedMessage {...appMessages.noAccessWorkspace} />,
       }));
       return;
     }

     const sortedWorkspaces = orderBy(workspaces, (workspace) =>
       `${workspace.name.trim().toLowerCase()}-${workspace.id}`, 'asc');

     if (selectedWorkspace) {
       const hasPermission = !!sortedWorkspaces.filter((workspace) =>
         workspace.id === selectedWorkspace).length;
       if (!hasPermission) {
         workspaceService.setSelectedWorkspace(sortedWorkspaces[0]);
       }
     } else {
       workspaceService.setSelectedWorkspace(sortedWorkspaces[0]);
     }
   }

   registerServices = (connection) => {
     accountService.register(connection);
     activityService.register(connection);
     annotationService.register(connection);
     scenarioService.register(connection);
     pluginService.register(connection);
     templateService.register(connection);
     pathwayService.register(connection);
     interactiveService.register(connection);
     componentService.register(connection);
     assetManager.register(connection);
     knowledgeService.register(connection);
     projectService.register(connection);
     favoritesService.register(connection);
     recentService.register(connection);
     scopeService.register(connection);
     themeService.register(connection);
     publicationService.register(connection);
     exportService.register(connection);
     previewService.register(connection);
     gtmService.register(connection);
     undoRedoService.register();
     aiSummaryService.register(connection);
   };

  getCurrentUser = async (connection) => {
    const responseMsg = await connection.fetch(RTM_ME_GET);
    const currentUser = responseMsg.response.account;
    return currentUser;
  }

   connectRTM = async (credentials, remember) => {
     const { dispatch } = this.props;
     try {
       const connection = await this.connectionManager.openConnection(credentials);
       this.setState({ connection: connection });
       this.isAutoConnecting = false;
       this.registerServices(connection);
       const currentUser = await this.getCurrentUser(connection);
       if (currentUser) {
         //intializing featureFlags with valid user.
         await featureFlagService.initializeFeatureFlags(currentUser);
         this.resolveIsUserInfoAvailablePromise?.(currentUser);
       }
      
       this.fireGTMLoginEvent(currentUser);
       

       dispatch(serverConnect(
         credentials,
         remember,
         connection
       ));
       return connection;
     } catch (error) {
       this.isAutoConnecting = false;
       switch (error.constructor) {
         case AuthenticationFailedError:
           dispatch(serverConnectError(<FormattedMessage {...messages.credentialsError} />));
           break;
         case ConnectionFailedError:
           dispatch(serverConnectError(<FormattedMessage {...messages.connectionError} />));
           break;
         default:
           dispatch(serverConnectError(<FormattedMessage {...messages.defaultError} />));
       }
       return null;
     }
   }
   /**
    * Fire GTM user session environment event once the user logs in
    * @param currentUser - The current user
    */
   fireGTMLoginEvent = (currentUser) => {
     gtmService.setUserParams(currentUser);
   }

   handleLogin = async (credentials, remember) => {
     await this.connectRTM(credentials, remember);
   };

   handleLoginBronte = ({ email, password, remember }) => {
     const credentials = { email, password };
     this.handleLogin(credentials, remember, false);
   };

  handleLoginIesV2 = async (tokenInfo) => {
    const isIesV2Auth = load(IES_V2_AUTH, LOCAL_STORAGE) === 'true';

    if (isIesV2Auth) {
      const credentials = { iesV2Token: tokenInfo.bearerToken };
      await this.handleLogin(credentials, true, false);
    } else {
      this.redirectToIesV2Login();
    }
  }

  redirectToIesV2Login = () => {
    this.setState({ isIesV2Loading: true });
    const iesV2BaseURL = this.isIesV2SocialLoginEnabled() ? appConfig.getIesV2SocialLoginURL() : appConfig.getIesV2LoginURL();
    const bronteRedirectURL = appConfig.getIesV2LoginRedirectURL();
    window.location.href = `${iesV2BaseURL}${bronteRedirectURL}`;
    save(IES_V2_AUTH, true, LOCAL_STORAGE);
  }

  processIesV2Token = async () => {
    try {
      const isIesV2Auth = load(IES_V2_AUTH, LOCAL_STORAGE) === 'true';
      const iesV2AuthValue = load(IES_V2_AUTH_VALUE, LOCAL_STORAGE);

      if (isIesV2Auth && !iesV2AuthValue) {
        this.setState({ isIesV2Loading: true });
        const response = await fetch(appConfig.getAzureAdAuthorize(), {
          method: 'POST',
          credentials: 'include',
        });
        const tokenInfo = await response.json();
        
        if (tokenInfo.status === 400) {
          remove(IES_V2_AUTH, LOCAL_STORAGE);
          throw tokenInfo;
        }
        
        save(IES_V2_AUTH_VALUE, JSON.stringify(tokenInfo), LOCAL_STORAGE);
        await this.handleLoginIesV2(tokenInfo);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Something went wrong while Iesv2 login');

      remove(IES_V2_AUTH, LOCAL_STORAGE);
      remove(IES_V2_AUTH_VALUE, LOCAL_STORAGE);
      this.props.dispatch(pushToast({
        type: 'simple',
        message: <FormattedMessage {...appMessages.invalidLogin} />,
      }));
    } finally {
      this.setState({ isIesV2Loading: false });
    }
  }

   dismissNotification = () => {
     this.setState({ showBannerNotification : false });
     this.props.setNotificationHeight(0);
     save(`dismiss${this.state.notificationType}Time`, new Date(), SESSION_STORAGE);
   };

   setBannerNotificationDetails = (notificationType, notificationMessage = '') => {
     notificationMessage = notificationMessage.replace(/\n/g, "<br />");
     this.setState({ notificationType: notificationType,
       notificationMessage: notificationMessage,
       showBannerNotification : true,
     });
   };

   getBannerHeight = (bannerHeight) => {
     this.props.setNotificationHeight(bannerHeight);
     this.setState({bannerHeight: bannerHeight});
   }

   isDisplayNotificationBanner = (notificationType, updatedNotifcationDescription) => {
     const notificationDismissTime = load(`dismiss${notificationType}Time`, SESSION_STORAGE) || 0,
       notificationDismissMessage = load(`${notificationType}Message`, SESSION_STORAGE);
     const notificationDismissTimeInSec = new Date(notificationDismissTime).getTime()/1000;
     const currentTimeInSec = new Date().getTime()/1000;
     const diffInTimeInSec = currentTimeInSec - notificationDismissTimeInSec;
     if(!notificationDismissTime || diffInTimeInSec > NOTIFICATION_DISMISS_TIME || (notificationDismissMessage !== updatedNotifcationDescription)) {
       save(`${notificationType}Message`, updatedNotifcationDescription, SESSION_STORAGE);
       return true;
     }

     return false;
   }

   getBannerNotificationDetails = async () => {
     let notificationKeyData,
       notificationType = null;
     const { getFeatureFlagValue } = this.props;
     if(getFeatureFlagValue(featureKeys.FEATURE_FLAG_BRNT_19838_WORKSPACE_ALERT_BANNER_DO_NOT_DELETE)) {
       notificationKeyData = await getBannerNotificationFeatureKeyDetails(featureKeys.FEATURE_FLAG_BRNT_19838_WORKSPACE_ALERT_BANNER_DO_NOT_DELETE);
       notificationType = notificationTypes.ALERT;
     }
     if(getFeatureFlagValue(featureKeys.BRNT_19837_WORKSPACE_NOTIFICATION_BANNER_DO_NOT_DELETE)) {
       notificationKeyData = await getBannerNotificationFeatureKeyDetails(featureKeys.BRNT_19837_WORKSPACE_NOTIFICATION_BANNER_DO_NOT_DELETE);
       notificationType = notificationTypes.NOTIFICATION;
     }
     if(getFeatureFlagValue(featureKeys.BRNT_19839_WORKSPACE_WARNING_BANNER_DO_NOT_DELETE)) {
       notificationKeyData = await getBannerNotificationFeatureKeyDetails(featureKeys.BRNT_19839_WORKSPACE_WARNING_BANNER_DO_NOT_DELETE);
       notificationType = notificationTypes.WARNING;
     }

     if(notificationType && this.isDisplayNotificationBanner(notificationType, notificationKeyData.description)) {
       this.setBannerNotificationDetails(notificationType, notificationKeyData.description);
     }
   };

   isIesV2LoginEnabled = () => {
     const queryString = new URLSearchParams(this.props.location?.search);
     return queryString.get('iesV2') === 'true';
   }

   isIesV2SocialLoginEnabled = () => {
     const queryString = new URLSearchParams(this.props.location?.search);
     return queryString.get('social') === 'true';
   }

   setReviewerBanner = (flag) => {
     const { updateReviewerBanner } = this.props;
     updateReviewerBanner(flag);
   }

   render() {
     const { connection, workspaceTheme, currentUser, getFeatureFlagValue, areFeatureFlagsAvailable, showReviewerBanner} = this.props;  
     const { preferredTheme, notificationMessage, notificationType, showBannerNotification, bannerHeight } = this.state;
     const currentTheme = themes[workspaceTheme];

     if (!areFeatureFlagsAvailable) return null;

     //display NotificationBanner only if user is logged in.
     const displayNotificationBanner = showBannerNotification && currentUser.session;
     if (this.isAutoConnecting) {
       return null;
     }

     if (this.state.isIesV2Loading) {
       return <Loader />;
     }

     return (<ThemeProvider theme={currentTheme}>
       {showReviewerBanner &&
           <BannerNotification
             message={appMessages.reviewerWarning.defaultMessage}
             type={notificationType}
             theme={currentTheme}
             showOkButton = {false}
             onClose={this.dismissNotification}
             getBannerHeight={this.getBannerHeight}
             isReviewerBanner={true}> 
           </BannerNotification>}
       <GlobalStyle />
       {displayNotificationBanner &&
           <BannerNotification
             message={notificationMessage}
             type={notificationType}
             theme={currentTheme}
             onClose={this.dismissNotification}
             getBannerHeight={this.getBannerHeight}>
           </BannerNotification>}
       <AppWrapper showBannerNotification={displayNotificationBanner} bannerHeight={bannerHeight}>
         <ToastManager />
         {connection.connected ?
           <ServicesProvider
             activityService={activityService}
             annotationService={annotationService}
             scenarioService={scenarioService}
             pluginService={pluginService}
             templateService={templateService}
             pathwayService={pathwayService}
             interactiveService={interactiveService}
             componentService={componentService}
             projectService={projectService}
             recentService={recentService}
             favoritesService={favoritesService}
             scopeService={scopeService}
             themeService={themeService}
             aiSummaryService={aiSummaryService}
           >
             <FileUploadProvider
               bearerToken={currentUser.bearerToken}
               assetManager={assetManager}
             >
               <CopyContainer
                 interactiveService={interactiveService}
                 componentService={componentService}
                 assetManager={assetManager}
               >
                 <RouterMiddleware setReviewerBanner={this.setReviewerBanner} connection={this.state.connection}>
                   <Switch>
                     <Route exact path="/" component={this.props.getFeatureFlagValue(featureKeys.BRNT_18710_WORKSPACE_REDESIGN_22) ? RecentPage : HomePage} />
                     {currentUser.me?.roles.includes(roles.ADMIN) ? <Route path="/admin" component={Admin} /> : <Route path="/owner" component={Owner} />}
                     <Route path="/recent" component={RecentPage} />
                     <Route path="/favorites" component={FavoritesPage} />
                     <Route path="/courseware" component={Projects} />
                     <Route path="/dashboard" component={DashboardBuilderContainer} />
                     <Route path="/knowledge" component={Knowledge} />
                     <Route path="/plugins" component={Plugins} />
                     <Route path="/publications" component={Publications} />
                     <Route path="/classes" component={Cohorts} />
                     <Route path="/course/:courseId" component={CourseBuilderContainer} />
                     <Route path="/lesson/:lessonId" component={LessonBuilderContainer} />
                     <Route path="/activity/:activityId" component={ActivityBuilderContainer} />
                     {getFeatureFlagValue(featureKeys.CORNUCOPIA_PHASE_ONE) &&
                     <Route
                       path="/cornucopia/lesson/:lessonId/pathway/:pathwayId/screen/:screenId"
                       component={CornucopiaScreenEditor} />
                     }
                     <Route
                       path="/store"
                       render={() => (
                         <PlaceholderPage
                           pageTitle="Store"
                           selectedNavKey="store"
                           icon="duotoneStore"
                           title="Store coming soon"
                           message="We’re still working the store page. This will be where you manage published courseware and plugins."
                         />
                       )}
                     />
                     <Route path="/unauthorized" component={UnAuthorizedPage} />
                     <Route path="/unavailable" component={UnAvailableContent} />
                     <Redirect to="/" />
                   </Switch>
                 </RouterMiddleware>
               </CopyContainer>
             </FileUploadProvider>
           </ServicesProvider> :
           <LoginPage
             onSubmit={this.handleLoginBronte}
             errorMessage={connection.error}
             isLoading={!!connection.loading}
             isWorkspaceIesV2Enabled={this.isIesV2LoginEnabled() || getFeatureFlagValue(featureKeys.WORKSPACE_IES_V2)}
             iesV2Login={this.redirectToIesV2Login}
           />
         }
         <Helmet>
           <link rel="icon" href={preferredTheme === 'dark' ? faviconDark : faviconLight} />
           <style>
             {`
                 body {
                   background-color: ${currentTheme && currentTheme['color.background']};
                 }
               `}
           </style>
         </Helmet>
       </AppWrapper>
     </ThemeProvider>
     );
   }
}

App.propTypes = {
  dispatch: PropTypes.func.isRequired,
  setTheme: PropTypes.func.isRequired,
  connection: PropTypes.object,
  currentUser: PropTypes.shape({
    bearerToken: PropTypes.string,
    me: PropTypes.object,
  }),
  workspacesLoadingState: PropTypes.bool,
  workspaces: PropTypes.arrayOf(workspaceShape),
  workspaceTheme: PropTypes.string,
  showReviewerBanner : PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  currentUser: makeSelectCurrentUser(),
  connection: makeSelectConnectionManager(),
  workspaces: selectWorkspacesList,
  workspacesLoadingState: selectWorkspacesLoadingState,
  workspaceTheme: makeSelectWorkspaceTheme(),
  showReviewerBanner : getReviewBanner(),
});

export function mapDispatchToProps(dispatch) { // Exported for testing purposes.
  return {
    dispatch,
    setTheme: (themeId) => dispatch(changeWorkspaceTheme(themeId)),
    setNotificationHeight: (value) => dispatch(updateNotificationHeight(value)),
    updateReviewerBanner : (flag) => dispatch(toggleReviewerBanner(flag)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withConnectionSaga = injectSaga({ key: 'connection', saga: connectionSaga });
const withCurrentUserSaga = injectSaga({ key: 'currentUser', saga: currentUserSaga });
const withPluginLoaderSaga = injectSaga({ key: 'pluginLoader', saga: pluginLoader });

const flagNames = [
  featureKeys.FEATURE_FLAG_BRNT_19838_WORKSPACE_ALERT_BANNER_DO_NOT_DELETE,
  featureKeys.BRNT_19837_WORKSPACE_NOTIFICATION_BANNER_DO_NOT_DELETE,
  featureKeys.BRNT_19839_WORKSPACE_WARNING_BANNER_DO_NOT_DELETE,
  featureKeys.CORNUCOPIA_PHASE_ONE,
  featureKeys.BRNT_18710_WORKSPACE_REDESIGN_22,
  featureKeys.WORKSPACE_IES_V2,
];

export default compose(
  withRouter,
  withConnect,
  withConnectionSaga,
  withCurrentUserSaga,
  withPluginLoaderSaga,
  enhanceWithSessionExpiryCheck,
)(enhanceWithFeatureFlagSubscription(App, flagNames, true));
export class TestApp extends App { } // Exported for testing purposes.
